import {unsafeDefaultCopies} from '../template/templateBindings';
import type {DesignModuleSpecJson} from '../data/contracts';
import {supportedSpecVersion} from '../data/specVersion';
/**
 * UPT-2095 (§11.2) — contrôle LOCAL d'un Module Spec pour le dépôt de démonstration : fonction pure, sans IA, sans
 * réseau, qui reproduit une PARTIE des règles du validateur serveur (rest §4.3) sur un document JSON lu de façon
 * défensive. APPROXIMATION DE DÉMONSTRATION, documentée : ce n'est PAS le schéma complet (le serveur applique la
 * structure v1 / v1.1 en Java et les 13 + 4 + 1 règles) ; ici seulement :
 *  - `schema` : `specVersion` ∈ {1.0, 1.1, 1.2, 1.3}, `module` / `entities` objets, codes d'entités et noms de champs au motif
 *    du schéma, champ sans `name` / `type` ou type inconnu ;
 *  - `ref.field` : champs cités par `screens` (colonnes, tri, carte mobile, onglets), `businessKey`, `upsert`,
 *    `rules[].fields` et `relations[].source.field` existent dans l'entité ;
 *  - `ref.entity` : entités citées par `relations`, `pages`, `coreReuse`, `navigation` et `data.store` existent ;
 *  - `unique` : codes de champs (par entité), de relations, de pages et de rôles uniques ;
 *  - `workflow` : un seul état initial, transitions entre états déclarés ;
 *  - `protection.pii` : `personalData: true` sans `tokenize` ;
 *  - avertissements `i18n` (libellé sans `fr`), `screens` (entité sans colonnes de liste), `testplan.coverage`
 *    (entité, règle ou page non couverte ; sans `testPlan` : un seul avertissement) ;
 *  - statistiques (entités, champs, relations, pages, scènes, étapes), scores 0..100 et `seedProjection` ;
 *  - UPT-2079 L11 §9 (`schema`) : les trois erreurs mécaniques que la réparation sait corriger — état de workflow avec `code`, description d'écran de moins de
 *    20 caractères, domaine hors motif.
 * Rapport : forme `ValidationReport` du contrat, erreurs puis avertissements, chaque liste triée par `code` puis
 * `path` ; messages « fr — en » comme le serveur. Toute chaîne reste une donnée : rien n'est interprété.
 */
import type { ModuleSpecDocument, ValidationIssue, ValidationReport } from '../data/moduleSpecContracts';
import { DOMAIN_PATTERN, PAGE_DESCRIPTION_MIN } from './moduleSpecLocalRepair';

type Json = Record<string, unknown>;

const ENTITY_CODE = /^[a-z][a-z0-9]{1,40}$/;
const FIELD_NAME = /^[a-z][a-z0-9_]{0,62}$/;

/** `$defs.field.type` du schéma v1.1 (liste fermée). */
export const MODULE_SPEC_FIELD_TYPES: readonly string[] = [
  'text', 'textarea', 'number', 'amount', 'date', 'datetime', 'time', 'checkbox', 'select', 'radio', 'typeahead',
  'email', 'color', 'link', 'display', 'hidden', 'attachments', 'references', 'entityref',
];

const asRecord = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const records = (value: unknown): Json[] => asArray(value).map(asRecord).filter((r): r is Json => r !== null);

const issue = (code: string, path: string, fr: string, en: string): ValidationIssue => ({
  code,
  path,
  message: `${fr} — ${en}`,
});

const byCodeThenPath = (left: ValidationIssue, right: ValidationIssue): number =>
  left.code.localeCompare(right.code) || left.path.localeCompare(right.path);

const clamp = (value: number): number => Math.max(0, Math.min(100, Math.round(value)));

/** Noms des champs déclarés d'une entité (sans les entrées malformées). */
const fieldNames = (entity: Json): string[] =>
  records(entity.fields).map((field) => asString(field.name)).filter((name) => name.length > 0);

export function moduleSpecLocalCheck(input: ModuleSpecDocument | unknown): ValidationReport {
  const errors: ValidationIssue[] = [];
  if(input&&typeof input==='object'&&unsafeDefaultCopies(input as DesignModuleSpecJson))errors.push(issue('template.binding_protection_review','/pages','Copie vers un champ moins protégé interdite.','Copy to a less protected field is forbidden.'));
  const warnings: ValidationIssue[] = [];
  const spec = asRecord(input);
  const stats = { entities: 0, fields: 0, relations: 0, pages: 0, scenes: 0, steps: 0 };

  if (!spec) {
    errors.push(issue('schema', '', 'Le document doit être un objet JSON.', 'The document must be a JSON object.'));
    return {
      valid: false,
      errors,
      warnings,
      scores: { coverage: 0, i18n: 0, reuse: 0, protection: 0 },
      stats,
      seedProjection: 'errors',
    };
  }

  if (!supportedSpecVersion(spec.specVersion)) {
    errors.push(issue('schema', '/specVersion', 'specVersion doit valoir « 1.0 », « 1.1 », « 1.2 » ou « 1.3 ».', 'specVersion must be "1.0", "1.1", "1.2" or "1.3".'));
  }
  const module = asRecord(spec.module);
  if (!module) {
    errors.push(issue('schema', '/module', 'module doit être un objet.', 'module must be an object.'));
  }
  if (module && typeof module.domain === 'string' && !DOMAIN_PATTERN.test(module.domain)) {
    errors.push(issue('schema', '/module/domain', `ne respecte pas le motif ${DOMAIN_PATTERN.source}`, `does not match pattern ${DOMAIN_PATTERN.source}`));
  }
  const entities = asRecord(spec.entities);
  if (!entities) {
    errors.push(issue('schema', '/entities', 'entities doit être un objet.', 'entities must be an object.'));
  }
  const entityMap = entities ?? {};
  const entityCodes = Object.keys(entityMap);
  const known = (code: string): boolean => entityCodes.includes(code) || code.startsWith('core:');

  // Libellés : objets par langue (fr obligatoire) ; une clé i18n (chaîne) est admise.
  const labelSlots: { path: string; value: unknown }[] = [];
  const checkI18n = (): void => {
    labelSlots.forEach(({ path, value }) => {
      const record = asRecord(value);
      if (record && typeof record.fr !== 'string') {
        warnings.push(issue('i18n', path, 'Libellé sans texte français (fr).', 'Label without French text (fr).'));
      }
    });
  };

  const coverable = new Set<string>();
  const piiFields: { tokenized: boolean }[] = [];
  let seedOk = true;

  entityCodes.forEach((code) => {
    const entity = asRecord(entityMap[code]);
    const base = `/entities/${code}`;
    if (!ENTITY_CODE.test(code)) {
      errors.push(issue('schema', base, 'Code d\'entité hors motif ^[a-z][a-z0-9]{1,40}$.', 'Entity code does not match ^[a-z][a-z0-9]{1,40}$.'));
    }
    if (!entity) {
      errors.push(issue('schema', base, 'L\'entité doit être un objet.', 'The entity must be an object.'));
      return;
    }
    stats.entities += 1;
    coverable.add(code);
    const label = asRecord(entity.label);
    if (label) {
      labelSlots.push({ path: `${base}/label/singular`, value: label.singular });
      labelSlots.push({ path: `${base}/label/plural`, value: label.plural });
    }
    const fields = records(entity.fields);
    const names = new Set<string>();
    fields.forEach((field, index) => {
      stats.fields += 1;
      const fieldPath = `${base}/fields/${index}`;
      const name = asString(field.name);
      const type = asString(field.type);
      if (!name) {
        errors.push(issue('schema', `${fieldPath}/name`, 'Champ sans nom.', 'Field without name.'));
      } else if (!FIELD_NAME.test(name)) {
        errors.push(issue('schema', `${fieldPath}/name`, 'Nom de champ hors motif ^[a-z][a-z0-9_]{0,62}$.', 'Field name does not match ^[a-z][a-z0-9_]{0,62}$.'));
      } else if (names.has(name)) {
        errors.push(issue('unique', `${fieldPath}/name`, `Nom de champ en double : ${name}.`, `Duplicate field name: ${name}.`));
      } else {
        names.add(name);
      }
      if (!type) {
        errors.push(issue('schema', `${fieldPath}/type`, 'Champ sans type.', 'Field without type.'));
        seedOk = false;
      } else if (!MODULE_SPEC_FIELD_TYPES.includes(type)) {
        errors.push(issue('schema', `${fieldPath}/type`, `Type de champ inconnu : ${type}.`, `Unknown field type: ${type}.`));
        seedOk = false;
      }
      if (field.label !== undefined) {
        labelSlots.push({ path: `${fieldPath}/label`, value: field.label });
      }
      const protection = asRecord(field.protection);
      const typed = ['number','amount','percentage','date','datetime','time','integer','decimal','bigint','numeric'].includes(type);
      const textLike = ['text','textarea','richtext','email','phone','color','link','select','radio','typeahead','multiselect'].includes(type);
      if (typed && protection?.tokenize) errors.push(issue('protection.pii', `${fieldPath}/protection/tokenize`, 'Les nombres, montants et dates restent typés. Retirez tokenize et conservez C3 + personalData ; tokenisez uniquement les textes et listes.', 'Numbers, amounts and dates remain typed. Remove tokenize and retain C3 + personalData; tokenize only text and lists.'));
      if (protection && protection.personalData === true) {
        const tokenized = typeof protection.tokenize === 'string' && protection.tokenize.length > 0;
        piiFields.push({ tokenized });
        if (typed ? !['C3','C4'].includes(asString(protection.classification)) : textLike && !tokenized) {
          errors.push(issue('protection.pii', `${fieldPath}/protection`, 'Texte personnel : ajoutez tokenize ; nombre ou date : conservez C3 + personalData sans tokenize.', 'Personal text: add tokenize; number or date: retain C3 + personalData without tokenize.'));
        }
      }
      const data = asRecord(field.data);
      if (data && data.type === 'store') {
        const target = asString(data.entity);
        if (target && !known(target)) {
          errors.push(issue('ref.entity', `${fieldPath}/data/entity`, `Entité inconnue : ${target}.`, `Unknown entity: ${target}.`));
        }
      }
    });

    const declared = fieldNames(entity);
    const requireField = (name: string, path: string): void => {
      if (!declared.includes(name)) {
        errors.push(issue('ref.field', path, `Champ inconnu dans ${code} : ${name}.`, `Unknown field in ${code}: ${name}.`));
      }
    };
    if (typeof entity.businessKey === 'string') {
      requireField(entity.businessKey, `${base}/businessKey`);
    }
    asArray(entity.upsert).forEach((name, index) => {
      if (typeof name === 'string') requireField(name, `${base}/upsert/${index}`);
    });

    const screens = asRecord(entity.screens);
    const list = screens ? asRecord(screens.list) : null;
    const columns = list ? asArray(list.columns) : [];
    if (columns.length === 0) {
      warnings.push(issue('screens', `${base}/screens`, 'Entité sans colonnes de liste (les pages génériques en dériveront par défaut).', 'Entity without list columns (generic pages will derive them).'));
    }
    columns.forEach((name, index) => {
      if (typeof name === 'string') requireField(name, `${base}/screens/list/columns/${index}`);
    });
    const sort = list ? asRecord(list.defaultSort) : null;
    if (sort && typeof sort.field === 'string') {
      requireField(sort.field, `${base}/screens/list/defaultSort/field`);
    }
    const card = list ? asRecord(list.mobileCard) : null;
    if (card) {
      (['title', 'subtitle', 'badge'] as const).forEach((slot) => {
        if (typeof card[slot] === 'string') requireField(card[slot] as string, `${base}/screens/list/mobileCard/${slot}`);
      });
    }
    const form = screens ? asRecord(screens.form) : null;
    records(form?.tabs).forEach((tab, tabIndex) => {
      asArray(tab.fields).forEach((name, index) => {
        if (typeof name === 'string') requireField(name, `${base}/screens/form/tabs/${tabIndex}/fields/${index}`);
      });
    });

    records(entity.rules).forEach((rule, ruleIndex) => {
      const key = asString(rule.key);
      if (key) coverable.add(`rule:${key}`);
      asArray(rule.fields).forEach((name, index) => {
        if (typeof name === 'string') requireField(name, `${base}/rules/${ruleIndex}/fields/${index}`);
      });
    });

    const workflow = asRecord(entity.workflow);
    if (workflow) {
      const states = records(workflow.states);
      states.forEach((state, stateIndex) => {
        if ('code' in state) {
          errors.push(issue('schema', `${base}/workflow/states/${stateIndex}/code`, 'propriété inconnue « code »', 'unknown property “code”'));
        }
      });
      const keys = states.map((state) => asString(state.key)).filter((key) => key.length > 0);
      const initial = states.filter((state) => state.initial === true).length;
      if (states.length > 0 && initial !== 1) {
        errors.push(issue('workflow', `${base}/workflow/states`, `Un seul état initial attendu (${initial} trouvé).`, `Exactly one initial state expected (${initial} found).`));
      }
      records(workflow.transitions).forEach((transition, index) => {
        (['from', 'to'] as const).forEach((slot) => {
          const state = asString(transition[slot]);
          if (state && !keys.includes(state)) {
            errors.push(issue('workflow', `${base}/workflow/transitions/${index}/${slot}`, `État non déclaré : ${state}.`, `Undeclared state: ${state}.`));
          }
        });
      });
    }
  });

  // Relations.
  const relationCodes = new Set<string>();
  records(spec.relations).forEach((relation, index) => {
    stats.relations += 1;
    const path = `/relations/${index}`;
    const code = asString(relation.code);
    if (code) {
      if (relationCodes.has(code)) {
        errors.push(issue('unique', `${path}/code`, `Code de relation en double : ${code}.`, `Duplicate relation code: ${code}.`));
      }
      relationCodes.add(code);
    }
    const source = asRecord(relation.source);
    const target = asRecord(relation.target);
    const sourceEntity = source ? asString(source.entity) : '';
    if (sourceEntity && !known(sourceEntity)) {
      errors.push(issue('ref.entity', `${path}/source/entity`, `Entité inconnue : ${sourceEntity}.`, `Unknown entity: ${sourceEntity}.`));
    } else if (source && typeof source.field === 'string') {
      const owner = asRecord(entityMap[sourceEntity]);
      if (owner && !fieldNames(owner).includes(source.field)) {
        errors.push(issue('ref.field', `${path}/source/field`, `Champ inconnu dans ${sourceEntity} : ${source.field}.`, `Unknown field in ${sourceEntity}: ${source.field}.`));
      }
    }
    const targetEntity = target ? asString(target.entity) : '';
    if (targetEntity && !(target && typeof target.module === 'string') && !known(targetEntity)) {
      errors.push(issue('ref.entity', `${path}/target/entity`, `Entité inconnue : ${targetEntity}.`, `Unknown entity: ${targetEntity}.`));
    }
  });

  // Pages, socle réutilisé, navigation, rôles.
  const pageKeys = new Set<string>();
  const declaredPageKeys = new Set(records(spec.pages).map((page) => asString(page.key)).filter((key) => key.length > 0));
  const declaredPageSlugs = records(spec.pages).map((page) => asString(page.slug) || asString(page.key));
  const reservedSlugs = new Set<string>(['dashboard', 'onboarding', 'new']);
  entityCodes.forEach((code) => {
    reservedSlugs.add(code);
    const slugs = asRecord(asRecord(entityMap[code])?.slugs);
    [asString(slugs?.singular), asString(slugs?.plural)].forEach((slug) => {
      if (slug) reservedSlugs.add(slug);
    });
  });
  /**
   * UPT-2079 L14b (rest §4.7) — règles `page.*` d'une page du document, sans l'inventaire du prototype (`page.prototype_ref` est l'affaire du serveur) :
   * références d'objet (`ref.entity`), page cible, état cible, emplacement, liaison sans objet, adresse déjà prise, page sur mesure vide ou sans prototype.
   */
  const checkCustomPage = (page: Json, index: number): void => {
    const base = `/pages/${index}`;
    const refEntity = (path: string, code: unknown): void => {
      if (typeof code === 'string' && code.length > 0 && !known(code)) {
        errors.push(issue('ref.entity', path, `Entité inconnue : ${code}.`, `Unknown entity: ${code}.`));
      }
    };
    (['reads', 'writes'] as const).forEach((key) =>
      asArray(page[key]).forEach((code, position) => refEntity(`${base}/${key}/${position}`, code))
    );
    const slots = records(page.slots);
    const slotKeys = new Set<string>();
    slots.forEach((slot, position) => {
      const key = asString(slot.key);
      if (key) {
        if (slotKeys.has(key)) {
          errors.push(issue('page.slot_unknown', `${base}/slots/${position}/key`, `Clé d'emplacement en double : ${key}.`, `Duplicate slot key: ${key}.`));
        }
        slotKeys.add(key);
      }
      refEntity(`${base}/slots/${position}/entity`, slot.entity);
      const binding = asString(slot.binding);
      if (binding && binding !== 'none' && !asString(slot.entity)) {
        errors.push(issue('page.binding_entity', `${base}/slots/${position}`, 'Liaison de données sans objet.', 'Data binding without an entity.'));
      }
    });
    const actionKeys = new Set<string>();
    records(page.actions).forEach((action, position) => {
      const path = `${base}/actions/${position}`;
      const key = asString(action.key);
      if (key) {
        if (actionKeys.has(key)) {
          errors.push(issue('page.slot_unknown', `${path}/key`, `Clé d'action en double : ${key}.`, `Duplicate action key: ${key}.`));
        }
        actionKeys.add(key);
      }
      refEntity(`${path}/entity`, action.entity);
      const targetPage = asString(action.targetPage);
      if (targetPage && !declaredPageKeys.has(targetPage)) {
        errors.push(issue('page.target_unknown', `${path}/targetPage`, `Page cible inconnue : ${targetPage}.`, `Unknown target page: ${targetPage}.`));
      }
      const slot = asString(action.slot);
      if (slot && !slotKeys.has(slot)) {
        errors.push(issue('page.slot_unknown', `${path}/slot`, `Emplacement inconnu : ${slot}.`, `Unknown slot: ${slot}.`));
      }
      const transition = asString(action.transition);
      if (transition) {
        const states = records(asRecord(asRecord(entityMap[asString(action.entity)])?.workflow)?.states).map((state) => asString(state.key));
        if (!states.includes(transition)) {
          errors.push(issue('page.transition_unknown', `${path}/transition`, `État cible absent du cycle de vie de l'objet : ${transition}.`, `Target state missing from the entity workflow: ${transition}.`));
        }
      }
    });
    if (page.kind !== 'custom') return;
    const slug = asString(page.slug) || asString(page.key);
    if (slug && (reservedSlugs.has(slug) || declaredPageSlugs.filter((candidate) => candidate === slug).length > 1)) {
      errors.push(issue('page.slug_taken', `${base}/slug`, `Adresse de page déjà prise : ${slug}.`, `Page address already taken: ${slug}.`));
    }
    if (slots.length === 0 && asArray(page.actions).length === 0) {
      warnings.push(issue('page.custom_empty', base, 'Page sur mesure sans emplacement ni action.', 'Custom page without slots or actions.'));
    }
    if (!asRecord(page.prototypeRef) && !asString(page.mockupRef)) {
      warnings.push(issue('page.no_prototype_ref', base, 'Page sur mesure sans référence au prototype.', 'Custom page without a prototype reference.'));
    }
  };
  records(spec.pages).forEach((page, index) => {
    stats.pages += 1;
    const key = asString(page.key);
    if (key) {
      if (pageKeys.has(key)) {
        errors.push(issue('unique', `/pages/${index}/key`, `Clé de page en double : ${key}.`, `Duplicate page key: ${key}.`));
      }
      pageKeys.add(key);
      coverable.add(`page:${key}`);
    }
    if (typeof page.description === 'string' && page.description.length < PAGE_DESCRIPTION_MIN) {
      errors.push(issue('schema', `/pages/${index}/description`, `au moins ${PAGE_DESCRIPTION_MIN} caractères`, `at least ${PAGE_DESCRIPTION_MIN} characters`));
    }
    if (page.label !== undefined) labelSlots.push({ path: `/pages/${index}/label`, value: page.label });
    asArray(page.entities).forEach((entity, entityIndex) => {
      if (typeof entity === 'string' && !known(entity)) {
        errors.push(issue('ref.entity', `/pages/${index}/entities/${entityIndex}`, `Entité inconnue : ${entity}.`, `Unknown entity: ${entity}.`));
      }
    });
    checkCustomPage(page, index);
  });
  const reuse = records(spec.coreReuse);
  reuse.forEach((entry, index) => {
    asArray(entry.entities).forEach((entity, entityIndex) => {
      if (typeof entity === 'string' && !known(entity)) {
        errors.push(issue('ref.entity', `/coreReuse/${index}/entities/${entityIndex}`, `Entité inconnue : ${entity}.`, `Unknown entity: ${entity}.`));
      }
    });
  });
  const navigation = asRecord(spec.navigation);
  records(navigation?.menus).forEach((menu, menuIndex) => {
    records(menu.items).forEach((item, itemIndex) => {
      const entity = asString(item.entity);
      if (entity && !known(entity)) {
        errors.push(issue('ref.entity', `/navigation/menus/${menuIndex}/items/${itemIndex}/entity`, `Entité inconnue : ${entity}.`, `Unknown entity: ${entity}.`));
      }
    });
  });
  const permissions = asRecord(spec.permissions);
  const roleKeys = new Set<string>();
  records(permissions?.roles).forEach((role, index) => {
    const key = asString(role.key);
    if (key) {
      if (roleKeys.has(key)) {
        errors.push(issue('unique', `/permissions/roles/${index}/key`, `Clé de rôle en double : ${key}.`, `Duplicate role key: ${key}.`));
      }
      roleKeys.add(key);
    }
  });

  // Recette et couverture.
  const testPlan = asRecord(spec.testPlan);
  const covered = new Set<string>();
  if (!testPlan) {
    warnings.push(issue('testplan.coverage', '/testPlan', 'Aucun plan de recette : couverture 0 %.', 'No test plan: coverage 0%.'));
  } else {
    records(testPlan.scenes).forEach((scene) => {
      stats.scenes += 1;
      records(scene.steps).forEach((step) => {
        stats.steps += 1;
        asArray(step.covers).forEach((item) => {
          if (typeof item !== 'string') return;
          covered.add(item);
          const dot = item.indexOf('.');
          if (dot > 0 && !item.includes(':')) covered.add(item.slice(0, dot));
        });
      });
    });
    [...coverable].sort().forEach((item) => {
      if (!covered.has(item)) {
        const path = item.startsWith('rule:')
          ? `/testPlan`
          : item.startsWith('page:')
            ? `/pages`
            : `/entities/${item}`;
        warnings.push(issue('testplan.coverage', path, `Non couvert par la recette : ${item}.`, `Not covered by the test plan: ${item}.`));
      }
    });
  }
  checkI18n();

  // Scores (approximation de démonstration).
  const coverageScore = coverable.size === 0 || !testPlan
    ? 0
    : clamp(([...coverable].filter((item) => covered.has(item)).length / coverable.size) * 100);
  const locales = asArray(module?.locales).filter((locale): locale is string => typeof locale === 'string');
  const i18nScore = (() => {
    const objects = labelSlots.map(({ value }) => asRecord(value)).filter((record): record is Json => record !== null);
    if (objects.length === 0) return 100;
    const wanted = locales.length > 0 ? locales : ['fr'];
    const ratio = objects.reduce((sum, record) => sum + wanted.filter((locale) => typeof record[locale] === 'string').length / wanted.length, 0);
    return clamp((ratio / objects.length) * 100);
  })();
  const reuseScore = reuse.length === 0 ? 0 : clamp(Math.min(100, reuse.length * 34));
  const protectionScore = piiFields.length === 0 ? 100 : clamp((piiFields.filter((field) => field.tokenized).length / piiFields.length) * 100);

  errors.sort(byCodeThenPath);
  warnings.sort(byCodeThenPath);
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    scores: { coverage: coverageScore, i18n: i18nScore, reuse: reuseScore, protection: protectionScore },
    stats,
    seedProjection: seedOk && errors.length === 0 ? 'ok' : 'errors',
  };
}
